import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom';

import { AuthTokenProvider } from 'App/contexts/AuthTokenContext';
import { UserChecker, UserContextProvider } from 'App/contexts/UserContext';
import { UserPreferencesProvider } from 'App/contexts/UserPreferencesContext';
import TokenManagerService from 'App/services/TokenManagerService';
import UserService from 'App/services/UserService';
import PageSpinner from 'App/components/PageSpinner';
import WithHeader from 'App/components/WithHeader';
import { DynatraceDefineUser } from 'App/components/Dynatrace/DynatraceDefineUser';
import SessionStorageService from 'App/services/SessionStorageService';
import AuthService from 'App/services/AuthService';
import * as routeLookup from 'App/common/routeLookup';

const propTypes = {};

const defaultProps = {};

function isSessionIntact() {
    // Token exists AND critical localStorage keys AND UserService has a loaded user
    return Boolean(
        TokenManagerService.getAccessToken() &&
        window.localStorage.getItem('userId') &&
        window.localStorage.getItem('branchId') &&
        window.localStorage.getItem('companyId') &&
        UserService.get()?.id
    );
}

const ROUTES_WITH_NO_HEADER = [routeLookup.contentMonographRoute];

function AuthenticatedLayout() {
    const location = useLocation();

    const isNoHeader = ROUTES_WITH_NO_HEADER.some((path) => matchPath({ path, end: true }, location.pathname));

    const [status, setStatus] = useState(() => {
        // If we have a token, optimistically try 'ready' —
        // UserChecker will catch any missing user data below.
        if (TokenManagerService.getAccessToken()) {
            return isSessionIntact() ? 'ready' : 'loading';
        }
        return 'loading';
    });

    useEffect(() => {
        if (status === 'ready') return;

        // Consume SSO cookie if present — this sets the token and clears the logout flag
        if (Cookies.get('sso_jwt')) {
            SessionStorageService.getJwt(); // consumes cookie, calls setTokens, clears LOGOUT_FLAG
        }

        if (TokenManagerService.isLoggedOut()) {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setStatus('unauthenticated');
            return;
        }

        // If we already have a token in memory (e.g., just logged in via Angular),
        // don't call refresh — it would race with or invalidate the fresh token.
        if (TokenManagerService.getAccessToken()) {
            setStatus('ready');
            return;
        }

        // Only attempt refresh when there's genuinely no token
        let cancelled = false;
        TokenManagerService.refreshAccessToken()
            .then(() => {
                if (cancelled) return;
                if (!isSessionIntact()) {
                    TokenManagerService.clearTokens();
                    setStatus('unauthenticated');
                    return;
                }
                setStatus('ready');
            })
            .catch(() => {
                if (!cancelled) setStatus('unauthenticated');
            });

        return () => {
            cancelled = true;
        };
    }, [status]);

    if (status === 'loading') {
        return <PageSpinner />;
    }

    if (status === 'unauthenticated') {
        // Save the intended destination for deep link redirect after login
        if (location.pathname !== '/login' && location.pathname !== '/logout') {
            AuthService.setTargetPage(location.pathname + location.search + location.hash);
        }
        return <Navigate replace to={routeLookup.loginRoute} />;
    }

    // Allow deep-link target before rendering children
    if (status === 'ready') {
        const targetPage = AuthService.getTargetPage();
        if (targetPage) {
            AuthService.clearTargetPage();
            return <Navigate replace to={targetPage} />;
        }
    }

    return (
        <AuthTokenProvider>
            <UserContextProvider>
                <UserPreferencesProvider>
                    <UserChecker>
                        <DynatraceDefineUser />
                        {isNoHeader ? <Outlet /> : <WithHeader><Outlet /></WithHeader>}
                    </UserChecker>
                </UserPreferencesProvider>
            </UserContextProvider>
        </AuthTokenProvider>
    );
}

AuthenticatedLayout.propTypes = propTypes;
AuthenticatedLayout.defaultProps = defaultProps;

export default AuthenticatedLayout;
